import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { FlaggedCohortContent, ScreeningFlag } from '../../data/screening-results.model';
import { ScreeningResultsService } from '../../services/screening-results/screening-results.service';
import { ComingSoon } from '../../ui/coming-soon/coming-soon';
import { SeverityPill } from '../../ui/severity-pill/severity-pill';
import { FlaggedContentCard } from '../flagged-content-card/flagged-content-card';

export type FlagFilter = 'all' | 'high' | 'missing' | 'breach';

interface FlagFilterChip {
  id: FlagFilter;
  label: string;
}

/**
 * Tab 2 — the content that tripped a rule.
 *
 * Per-rule rows, when the brief payload has them, render as the flag list. Otherwise each post
 * that produced the red-flag count is a horizontal card. Fields the payload does not carry stay
 * labelled placeholders. "No flags shown" and "no problems found" stay distinct.
 */
@Component({
  selector: 'scr-screen-content',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ComingSoon, FlaggedContentCard, SeverityPill],
  templateUrl: './screen-content.html',
  styleUrl: './screen-content.scss',
})
export class ScreenContent {

  readonly results = inject(ScreeningResultsService);

  readonly filter = signal<FlagFilter>('all');

  readonly loading = computed(() => this.results.cohortState() === 'loading');

  /** Posts behind the red-flag count when the brief payload has no per-rule rows. */
  readonly flaggedPosts = computed<readonly FlaggedCohortContent[]>(() => this.results.flaggedContent());

  constructor() {
    effect(() => {
      console.log('flaggedPosts', this.flaggedPosts());
    });
  }

  /** True when we can show the posts themselves, but not a per-rule flag list. */
  readonly showFlaggedPosts = computed(
    () => this.results.flags().length === 0 && this.flaggedPosts().length > 0,
  );

  /** True when the count is positive and neither per-rule rows nor post rows could be resolved. */
  readonly detailUnavailable = computed(
    () =>
      this.results.attribution() === 'counts-only' &&
      this.results.kpis().flags > 0 &&
      this.flaggedPosts().length === 0,
  );

  /** True when the cohort genuinely has nothing flagged. */
  readonly nothingFlagged = computed(
    () => this.results.cohortState() === 'ready' && this.results.kpis().flags === 0,
  );

  readonly filterChips = computed<FlagFilterChip[]>(() => {
    const flags = this.results.flags();
    return [
      { id: 'all', label: `All flags (${flags.length})` },
      { id: 'high', label: `High severity (${flags.filter((f) => f.severity === 'high').length})` },
      { id: 'missing', label: `Missing requirement (${flags.filter((f) => f.kind === 'missing').length})` },
      { id: 'breach', label: `Rule breach (${flags.filter((f) => f.kind === 'breach').length})` },
    ];
  });

  readonly visibleFlags = computed<readonly ScreeningFlag[]>(() => {
    const flags = this.results.flags();
    switch (this.filter()) {
      case 'high':
        return flags.filter((flag) => flag.severity === 'high');
      case 'missing':
        return flags.filter((flag) => flag.kind === 'missing');
      case 'breach':
        return flags.filter((flag) => flag.kind === 'breach');
      default:
        return flags;
    }
  });

  readonly headline = computed(() => {
    const kpis = this.results.kpis();
    return `${kpis.flags} of ${kpis.evaluated} evaluated items breached a rule or missed a requirement.`;
  });

  readonly detailUnavailableBody = computed(() => {
    const kpis = this.results.kpis();
    return `SHOUT AI has found ${kpis.flags} pieces of content with issues across this cohort. The per-flag breakdown — the rule that was broken, the evidence and the decision buttons — appears here as soon as the detail feed is connected.`;
  });

  setFilter(filter: FlagFilter): void {
    this.filter.set(filter);
  }

  creatorName(flag: ScreeningFlag): string {
    if (!flag.creatorId) {
      return 'Unknown creator';
    }
    return this.results.creators().find((c) => c.noodleCreatorId === flag.creatorId)?.name ?? 'Unknown creator';
  }

  ruleCaption(flag: ScreeningFlag): string {
    const lead = flag.kind === 'missing' ? 'Required rule not met' : 'Restriction breached';
    return flag.ruleIndex ? `${lead} · rule #${String(flag.ruleIndex).padStart(2, '0')}` : lead;
  }
}
